import moment from "moment";
import aes from "crypto-js/aes";
import encUtf8 from "crypto-js/enc-utf8";
import imageCompression from "browser-image-compression";
import {
  CANDIDATE_STATUS,
  QUALIFICATION_TYPES,
  encryptionKey,
  RELATIONSHIP,
  EMPLOYEE_TYPES
} from "../constants/constants";
import { getAddressDetails } from "./candidateDataFilters";
import { getStateName } from "./onBoardingDataFilters";
import { API_BASE_URL } from "../api/config";

export const b64toBlob = (b64Data, contentType, sliceSize) => {
  contentType = contentType || "";
  sliceSize = sliceSize || 512;

  var byteCharacters = atob(b64Data);
  var byteArrays = [];

  for (var offset = 0; offset < byteCharacters.length; offset += sliceSize) {
    var slice = byteCharacters.slice(offset, offset + sliceSize);

    var byteNumbers = new Array(slice.length);
    for (var i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i);
    }

    var byteArray = new Uint8Array(byteNumbers);

    byteArrays.push(byteArray);
  }

  var blob = new Blob(byteArrays, { type: contentType });
  return blob;
};

export const getStatusDescription = statusId => {
  const status = Object.values(CANDIDATE_STATUS).find(
    status => status.id === statusId
  );
  return status ? status.description : "";
};

export const getEmployeeTypeDescription = typeId => {
  const employeeTypeId = typeId ?? 1;
  const employeeType = Object.values(EMPLOYEE_TYPES).find(
    type => type.id === employeeTypeId
  );
  return employeeType ? employeeType.name : "";
};

export const getQualificationByID = qualificationId =>
  Object.keys(QUALIFICATION_TYPES).find(
    type => QUALIFICATION_TYPES[type] === qualificationId
  );

export const getFormattedDate = (date, time = false, format = "DD/MM/YYYY") => {
  const isValidDate = date && moment(date).isValid();
  const validDate = isValidDate
    ? time
      ? moment(date, "YYYY-MM-DDTHH:mm:ss").format(`${format} hh:mm A`)
      : moment(date, "YYYY-MM-DDTHH:mm:ss").format(`${format}`)
    : "";
  return validDate;
};

export const getCurrentYearAndMonth = () => {
  const date = new Date();
  const currentYear = date.getFullYear();
  const currentMonth = ("0" + (date.getMonth() + 1)).slice(-2);
  return currentYear + currentMonth;
};

export const getDateObject = date =>
  moment(date, "DD/MM/YYYY hh:mm A").toDate();
export const sortByDate = (a, b, order) => {
  if (order === "asc") {
    return Date.parse(getDateObject(a)) - Date.parse(getDateObject(b));
  }
  return Date.parse(getDateObject(b)) - Date.parse(getDateObject(a));
};

export const sortDatesWithEmptyValues = (a, b, order) => {
  if (order === "asc") {
    return (
      (b != "") - (a != "") || Date.parse(new Date(a)) - Date.parse(new Date(b))
    );
  }
  return (
    (b != "") - (a != "") || Date.parse(new Date(b)) - Date.parse(new Date(a))
  );
};
export const encrypt = message =>
  message ? aes.encrypt(message, encryptionKey).toString() : "";

export const decrypt = message =>
  message ? aes.decrypt(message, encryptionKey).toString(encUtf8) : "";

export const imageCompressor = async imageFile => {
  const options = {
    maxSizeMB: 1,
    maxWidthOrHeight: 1920,
    useWebWorker: true
  };
  try {
    var compressedFile = await imageCompression(imageFile, options);
  } catch (error) {
    console.log(error);
  }
  var file = new File([compressedFile], imageFile.name, {
    path: imageFile.name,
    type: imageFile.type,
    lastModified: Date.now()
  });
  return file;
};

export const getRelationshipDescription = relationshipId => {
  const relationship = Object.values(RELATIONSHIP).find(
    relationship => relationship.id === relationshipId
  );
  return relationship ? relationship.description : "";
};

export const getImage = (imageName, path = "images/ID") => {
  //return `http://localhost:3000/images/509327_Associate_ID.jpg`; //for localhost
  return `${API_BASE_URL}/${path}/${imageName}`;
};

export const getSerialIDNo = id => `P${getCurrentYearAndMonth()}12B5F${id}`;

export const getGenderName = (genderID, genderDetails) => {
  const genderObj = genderDetails.find(gender => gender.id === genderID);
  return genderObj ? genderObj.genderType : "";
};

export const getMaritalName = (
  maritalID,
  maritalDetails,
  unmarriedInPlaceOfSingle = false
) => {
  const maritalObj = maritalDetails.find(marital => marital.id === maritalID);
  let maritalName = maritalObj ? maritalObj.marital : "";

  return unmarriedInPlaceOfSingle && maritalName === "Single"
    ? "Unmarried"
    : maritalName;
};
export const getFullName = personalDetails => {
  return personalDetails
    ? [
        personalDetails.firstName,
        personalDetails.middleName,
        personalDetails.lastName
      ]
        .filter(name => name)
        .join(" ")
    : "";
};
export const getAddressAsString = (type, addresses, states) => {
  const {
    addressLine1,
    addressLine2,
    landmark,
    locality,
    city,
    pincode,
    state
  } = getAddressDetails(type, addresses);

  return [
    [
      addressLine1,
      addressLine2,
      landmark,
      locality,
      city,
      getStateName(state, states)
    ]
      .filter(name => name)
      .join(", "),
    pincode
  ]
    .filter(name => name)
    .join(" - ");
};

export const scrollToTopLeft = (isSmoothRequired = false) => {
  if (window && window.scrollTo) {
    const smoothRequiredProps = isSmoothRequired ? { behavior: "smooth" } : {};
    window.scrollTo({ top: 0, left: 0, ...smoothRequiredProps });
  }
};

export const getAge = dateOfBirth => {
  var diff = new Date().getTime() - new Date(dateOfBirth).getTime();
  return Math.floor(diff / (1000 * 60 * 60 * 24 * 365.25));
};

export const getLastOrganization = organizationDetails => {
  if (
    organizationDetails != null &&
    Array.isArray(organizationDetails) &&
    organizationDetails.length > 0
  ) {
    return (
      organizationDetails.find(
        organization => organization.lastOrganization === 1
      ) || {}
    );
  }
  return {};
};

export const getUpdatedValueByFieldType = (fieldType, fieldValue) => {
  if (fieldType === "checkbox") {
    return Number(fieldValue);
  } else if (fieldType === "date") {
    const isValidDate = moment(fieldValue).isValid();
    return isValidDate ? moment(fieldValue).format("YYYY-MM-DD") : null;
  } else {
    return typeof fieldValue === "number" ? fieldValue : fieldValue.trimStart();
  }
};

export const isDateExistsInbetween = (selectedDate, fromDate, toDate) => {
  return (
    (fromDate || toDate) &&
    moment(selectedDate).isBetween(fromDate, toDate, "days", "[]")
  );
};
